import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemText,
  Modal,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Games as GamesIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  Login as LoginIcon,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { getLessonGameGroups, getFlashGamesByGroupId } from '../services/gameService';
import { useAuth } from '../contexts/AuthContext';
import { LoginModal } from './LoginModal';

interface GameGroup {
  id: string;
  name: string;
  description?: string;
  created_at: string;
}

interface FlashGame {
  id: string;
  title: string;
  description?: string;
  embed_url: string;
  group_id: string;
  created_at: string;
  updated_at: string;
  game_type: string;
  thumbnail?: string;
}

const LessonGames: React.FC = () => {
  const [gameGroups, setGameGroups] = useState<GameGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<GameGroup | null>(null);
  const [games, setGames] = useState<FlashGame[]>([]);
  const [selectedGame, setSelectedGame] = useState<FlashGame | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [iframeError, setIframeError] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [iframeKey, setIframeKey] = useState(0);
  const [iframeTimeout, setIframeTimeout] = useState<NodeJS.Timeout | null>(null);
  const [modalReady, setModalReady] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  
  const { currentUser } = useAuth();

  useEffect(() => {
    loadGameGroups();
  }, []);

  useEffect(() => {
    if (selectedGroup) {
      loadGames(selectedGroup.id);
    }
  }, [selectedGroup]);

  // Cleanup timeout khi component unmount
  useEffect(() => {
    return () => {
      if (iframeTimeout) {
        clearTimeout(iframeTimeout);
      }
    };
  }, [iframeTimeout]);

  const loadGameGroups = async () => {
    try {
      setLoading(true);
      const data = await getLessonGameGroups();
      setGameGroups(data || []);
      if (data && data.length > 0) {
        setSelectedGroup(data[0]);
      }
    } catch (error) {
      console.error('Error loading game groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadGames = async (groupId: string) => {
    try {
      setLoading(true);
      const data = await getFlashGamesByGroupId(groupId);
      console.log('Loaded games:', data); // Debug log
      setGames(data || []);
    } catch (error) {
      console.error('Error loading games:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGameClick = (game: FlashGame) => {
    console.log('Selected game:', game); // Debug log
    
    // Đóng modal trước nếu đang mở
    if (isModalOpen) {
      setIsModalOpen(false);
      setSelectedGame(null);
      setIframeError(false);
      setIframeLoading(true);
      // Clear timeout
      if (iframeTimeout) {
        clearTimeout(iframeTimeout);
        setIframeTimeout(null);
      }
      
      // Đợi một chút rồi mở lại
      setTimeout(() => {
        setSelectedGame(game);
        setIsModalOpen(true);
        setModalReady(false);
        // Reset iframe key để reload game hoàn toàn
        setIframeKey(prev => prev + 1);
        setIframeLoading(true);
        setIframeError(false);
        
        // Đợi modal render xong
        setTimeout(() => {
          setModalReady(true);
        }, 50);
        
        // Set timeout mới
        const timeout = setTimeout(() => {
          console.log('Iframe load timeout');
          setIframeLoading(false);
          setIframeError(true);
        }, 10000); // 10 giây
        setIframeTimeout(timeout);
      }, 100); // Đợi 100ms
    } else {
      setSelectedGame(game);
      setIsModalOpen(true);
      setModalReady(false);
      // Reset iframe key để reload game
      setIframeKey(prev => prev + 1);
      setIframeLoading(true);
      setIframeError(false);
      
      // Đợi modal render xong
      setTimeout(() => {
        setModalReady(true);
      }, 50);
      
      // Set timeout để xử lý trường hợp iframe load quá lâu
      if (iframeTimeout) {
        clearTimeout(iframeTimeout);
      }
      const timeout = setTimeout(() => {
        console.log('Iframe load timeout');
        setIframeLoading(false);
        setIframeError(true);
      }, 10000); // 10 giây
      setIframeTimeout(timeout);
    }
  };

  const handleCloseModal = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmClose = () => {
    setIsModalOpen(false);
    setSelectedGame(null);
    setIframeError(false);
    setIframeLoading(true);
    setShowConfirmDialog(false);
    // Clear timeout khi đóng modal
    if (iframeTimeout) {
      clearTimeout(iframeTimeout);
      setIframeTimeout(null);
    }
  };

  const handleCancelClose = () => {
    setShowConfirmDialog(false);
  };

  const handleIframeLoad = () => {
    console.log('Iframe loaded successfully');
    setIframeLoading(false);
    // Clear timeout nếu iframe load thành công
    if (iframeTimeout) {
      clearTimeout(iframeTimeout);
      setIframeTimeout(null);
    }
  };

  const handleIframeError = () => {
    console.log('Iframe error occurred');
    setIframeError(true);
  };

  const getGameUrl = (url: string) => {
    // Kiểm tra URL có tồn tại không
    if (!url) {
      return '';
    }
    
    // Đảm bảo URL có protocol
    if (!url.startsWith('http')) {
      return `https://${url}`;
    }
    
    return url;
  };

  // Hiển thị Alert yêu cầu đăng nhập nếu chưa đăng nhập
  if (!currentUser) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 700,
            mb: 3
          }}
        >
          Trò chơi theo bài học
        </Typography>

        <Card 
          sx={{ 
            background: 'linear-gradient(145deg, #1a1a1a 0%, #2d2d2d 100%)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            border: '1px solid #333'
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box sx={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                mb: 3,
                boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)'
              }}>
                <GamesIcon sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                🔐 Yêu cầu đăng nhập
              </Typography>
              
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
                Tính năng "Trò chơi theo bài học" chỉ dành cho người dùng đã đăng nhập. 
                Vui lòng đăng nhập để truy cập các trò chơi tương tác và theo dõi tiến độ học tập của bạn.
              </Typography>
            </Box>

            <Alert 
              severity="info" 
              sx={{ 
                mb: 4, 
                maxWidth: 600, 
                mx: 'auto',
                borderRadius: 2,
                '& .MuiAlert-message': {
                  width: '100%'
                }
              }}
              icon={<LoginIcon sx={{ fontSize: 24 }} />}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'info.main' }}>
                Lợi ích khi đăng nhập:
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <CheckCircleIcon sx={{ color: '#4caf50', mr: 1.5, fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Chơi trò chơi tương tác
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <CheckCircleIcon sx={{ color: '#4caf50', mr: 1.5, fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Theo dõi tiến độ học tập
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <CheckCircleIcon sx={{ color: '#4caf50', mr: 1.5, fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Lưu trữ lịch sử chơi game
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <CheckCircleIcon sx={{ color: '#4caf50', mr: 1.5, fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Nhận điểm thưởng và thành tích
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Alert>

            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => setIsLoginModalOpen(true)}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                  },
                }}
                startIcon={<LoginIcon />}
              >
                Đăng nhập ngay
              </Button>
            </Box>
          </CardContent>
        </Card>

        <LoginModal
          open={isLoginModalOpen}
          onClose={() => setIsLoginModalOpen(false)}
        />
      </Box>
    );
  }

  // Kiểm tra quyền admin
  if (!currentUser.is_admin) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 700,
            mb: 3
          }}
        >
          Trò chơi theo bài học
        </Typography>

        <Card 
          sx={{ 
            background: 'linear-gradient(145deg, #1a1a1a 0%, #2d2d2d 100%)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            border: '1px solid #333'
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box sx={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                mb: 3,
                boxShadow: '0 8px 24px rgba(244, 67, 54, 0.3)'
              }}>
                <LockIcon sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                🔒 Quyền truy cập bị hạn chế
              </Typography>
              
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
                Tính năng "Trò chơi theo bài học" chỉ dành cho tài khoản Admin. 
                Vui lòng liên hệ quản trị viên để được cấp quyền truy cập.
              </Typography>
            </Box>

            <Alert 
              severity="warning" 
              sx={{ 
                mb: 4, 
                maxWidth: 600, 
                mx: 'auto',
                borderRadius: 2,
                '& .MuiAlert-message': {
                  width: '100%'
                }
              }}
              icon={<LockIcon sx={{ fontSize: 24 }} />}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'warning.main' }}>
                Thông tin về quyền truy cập:
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <CheckCircleIcon sx={{ color: '#4caf50', mr: 1.5, fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Tài khoản Admin có thể chơi game
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <CheckCircleIcon sx={{ color: '#4caf50', mr: 1.5, fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Theo dõi tiến độ học tập
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <CheckCircleIcon sx={{ color: '#4caf50', mr: 1.5, fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Quản lý trò chơi
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <CheckCircleIcon sx={{ color: '#4caf50', mr: 1.5, fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Cấp quyền cho người dùng
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Alert>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Vui lòng liên hệ quản trị viên để được cấp quyền Admin
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Danh sách nhóm trò chơi */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              height: 'fit-content',
              backgroundColor: '#1a1a1a',
              border: '1px solid #333',
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Typography 
                variant="h6" 
                gutterBottom 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  color: '#bcd4f5',
                  fontWeight: 600,
                  mb: 2
                }}
              >
                <GamesIcon sx={{ color: '#667eea', fontSize: 20 }} />
                Nhóm trò chơi
              </Typography>
              <List sx={{ p: 0 }}>
                {gameGroups.map((group) => (
                  <ListItem
                    button
                    key={group.id}
                    onClick={() => setSelectedGroup(group)}
                    selected={selectedGroup?.id === group.id}
                    sx={{
                      mb: 0.5,
                      borderRadius: 1,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(102, 126, 234, 0.1)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(102, 126, 234, 0.15)',
                        borderLeft: '3px solid #667eea',
                      }
                    }}
                  >
                    <ListItemText
                      primary={group.name}
                      secondary={group.description}
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontWeight: 500,
                          fontSize: '1rem',
                          color: '#bcd4f5',
                        },
                        '& .MuiListItemText-secondary': {
                          color: '#6a7b91',
                          fontSize: '0.85rem'
                        }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Danh sách trò chơi */}
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              backgroundColor: '#1a1a1a',
              border: '1px solid #333',
              borderRadius: 2,
              minHeight: '400px',
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Typography 
                variant="h6" 
                gutterBottom 
                sx={{ 
                  color: '#bcd4f5',
                  fontWeight: 600,
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <PlayIcon sx={{ color: '#667eea', fontSize: 20 }} />
                {selectedGroup ? `Trò chơi - ${selectedGroup.name}` : 'Chọn nhóm trò chơi'}
              </Typography>
              
              {loading ? (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    py: 6,
                    gap: 1
                  }}
                >
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      border: '2px solid #333',
                      borderTop: '2px solid #667eea',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    }}
                  />
                  <Typography variant="body2" color="#6a7b91">
                    Đang tải...
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {games.map((game) => (
                    <Grid item xs={12} sm={6} md={4} key={game.id}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          backgroundColor: '#2a2a2a',
                          border: '1px solid #333',
                          borderRadius: 1,
                          '&:hover': {
                            backgroundColor: '#333',
                            borderColor: '#667eea',
                          },
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                        onClick={() => handleGameClick(game)}
                      >
                        <CardContent sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <PlayIcon sx={{ fontSize: 20, color: 'white' }} />
                          </Box>
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography 
                              variant="subtitle1" 
                              sx={{ 
                                fontWeight: 500,
                                color: '#bcd4f5',
                                fontSize: '0.9rem',
                                mb: 0.5,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {game.title}
                            </Typography>
                            {game.description && (
                              <Typography 
                                variant="body2" 
                                sx={{
                                  fontSize: '0.75rem',
                                  lineHeight: 1.2,
                                  color: '#6a7b91',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {game.description}
                              </Typography>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Modal chứa iframe trò chơi */}
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Paper
          sx={{
            width: '90vw',
            height: '90vh',
            maxWidth: 1200,
            maxHeight: 800,
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 2,
            backgroundColor: '#1a1a1a',
            border: '1px solid #333',
          }}
        >
          {/* Header với thông tin game */}
          {selectedGame && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 2,
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                borderBottom: '1px solid #333',
                p: 2,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flexGrow: 1, mr: 2 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: '#bcd4f5', 
                      fontWeight: 600,
                      mb: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <PlayIcon sx={{ fontSize: 20, color: '#667eea' }} />
                    {selectedGame.title}
                  </Typography>
                  {selectedGame.description && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#6a7b91',
                        fontSize: '0.85rem',
                        lineHeight: 1.3
                      }}
                    >
                      {selectedGame.description}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    onClick={() => {
                      setIframeKey(prev => prev + 1);
                      setIframeLoading(true);
                      setIframeError(false);
                    }}
                    sx={{
                      bgcolor: '#333',
                      color: '#bcd4f5',
                      '&:hover': {
                        bgcolor: '#444',
                        color: '#667eea',
                      },
                      transition: 'all 0.2s ease',
                    }}
                    title="Tải lại trò chơi"
                  >
                    <RefreshIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => {
                      if (selectedGame) {
                        window.open(getGameUrl(selectedGame.embed_url), '_blank');
                      }
                    }}
                    sx={{
                      bgcolor: '#333',
                      color: '#bcd4f5',
                      '&:hover': {
                        bgcolor: '#444',
                        color: '#667eea',
                      },
                      transition: 'all 0.2s ease',
                    }}
                    title="Mở trong tab mới"
                  >
                    <PlayIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleCloseModal}
                    sx={{
                      bgcolor: '#333',
                      color: '#bcd4f5',
                      '&:hover': {
                        bgcolor: '#444',
                        color: '#667eea',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          )}
          
          {selectedGame && selectedGame.embed_url ? (
            <>
              {iframeLoading && !iframeError && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    p: 3,
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Đang tải trò chơi...
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Vui lòng đợi trong giây lát
                  </Typography>
                </Box>
              )}
              
              {iframeError ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    p: 3,
                  }}
                >
                  <Typography variant="h6" gutterBottom color="error">
                    Không thể tải trò chơi
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    Có thể do chính sách bảo mật của trình duyệt hoặc trò chơi không khả dụng.
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="primary">
                      <a 
                        href={getGameUrl(selectedGame.embed_url)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: 'inherit', textDecoration: 'underline' }}
                      >
                        Mở trò chơi trong tab mới
                      </a>
                    </Typography>
                  </Box>
                </Box>
              ) : (
                modalReady && (
                  <iframe
                    key={iframeKey}
                    src={`${selectedGame.embed_url}&_t=${Date.now()}`}
                    style={{
                      width: '100%',
                      height: 'calc(100% - 80px)',
                      border: 'none',
                      display: iframeLoading ? 'none' : 'block',
                      maxWidth: '100%',
                      marginTop: '80px',
                    }}
                    title={selectedGame.title}
                    allowFullScreen
                    onError={handleIframeError}
                    onLoad={handleIframeLoad}
                  />
                )
              )}
            </>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                p: 3,
              }}
            >
              <Typography variant="h6" gutterBottom color="error">
                Không có URL trò chơi
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Trò chơi này chưa có URL hoặc URL không hợp lệ.
              </Typography>
            </Box>
          )}
        </Paper>
      </Modal>

      {/* Confirm Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelClose}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">
          Xác nhận đóng trò chơi
        </DialogTitle>
        <DialogContent>
          <Typography>
            Bạn có chắc chắn muốn đóng trò chơi này không? Tiến độ của bạn có thể bị mất.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClose} color="primary">
            Hủy
          </Button>
          <Button onClick={handleConfirmClose} color="error" variant="contained">
            Đóng
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
  };
  
  export default LessonGames; 